/**
 * SectionPresetEditor (iter-48) — Preset Templates page control for
 * authoring & managing front-matter / back-matter / chapter presets
 * without opening a book. Restores the "dropdown for each entry +
 * open space to paste the data" UX that lived only inside Manual
 * Compose before.
 *
 * Kinds:
 *   - front  → pick a canonical front-matter slot (Copyright, Dedication…)
 *   - back   → pick a canonical back-matter slot (About the Author…)
 *   - chapter → free-form chapter template (title + optional subtitle + body)
 *
 * Save writes to /api/manual-presets and updates the parent's cache.
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

// Kept in sync with /app/backend/ph360/manual_sections.py — do not rename keys.
const FRONT_SECTIONS = [
  ["title_page",            "Title Page"],
  ["copyright",             "Copyright"],
  ["publisher_note",        "Publisher's Note"],
  ["preface",               "Preface"],
  ["foreword",              "Foreword"],
  ["full_introduction",     "Introduction"],
  ["acknowledgments",       "Acknowledgments"],
  ["dedication",            "Dedication"],
  ["about_author_front",    "About the Author (Front)"],
  ["general_disclaimer",    "General Disclaimer"],
  ["medical_disclaimer",    "Medical Disclaimer"],
  ["financial_disclaimer",  "Financial Disclaimer"],
  ["legal_disclaimer",      "Legal Disclaimer"],
  ["ai_disclosure",         "AI Disclosure"],
  ["about_research_team",   "About the Research Team"],
];
const BACK_SECTIONS = [
  ["companion_resource",    "Companion Resource / Worksheet CTA"],
  ["recommended_reads",     "Recommended Next Reads"],
  ["review_request",        "Review Request / Share This Guide"],
  ["about_the_author",      "About the Author"],
  ["about_publishinghub",   "About PublishingHub360"],
  ["closing_note",          "Publisher's Closing Note"],
];

const KIND_META = {
  front:   { label: "Front-matter",  sections: FRONT_SECTIONS },
  back:    { label: "Back-matter",   sections: BACK_SECTIONS },
  chapter: { label: "Chapter",       sections: null }, // free-form
};

export function SectionPresetEditor({ presets, team, workspaces, onCreated, onDeleted }) {
  const [expanded, setExpanded] = useState(true);
  const [kind, setKind] = useState("front");
  const [sectionKey, setSectionKey] = useState(FRONT_SECTIONS[0][0]);
  const [customKey, setCustomKey] = useState("");   // iter-51 custom slot key
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  // iter-101 — sharing scope for the CREATE form: team is a checkbox
  // (single choice), workspaces are individually selectable
  // checkboxes (multi-select).
  const [teamOn, setTeamOn] = useState(false);
  const [workspaceIds, setWorkspaceIds] = useState(() => new Set());
  const toggleWorkspace = (wid) => {
    setWorkspaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(wid)) next.delete(wid); else next.add(wid);
      return next;
    });
  };
  // Which library rows are currently expanded to reveal their content.
  const [openRows, setOpenRows] = useState({});

  const meta = KIND_META[kind];
  const isCustomSlot = sectionKey === "__custom__";
  const sectionMeta = useMemo(() => {
    if (!meta.sections) return null;
    if (isCustomSlot) return null;
    return meta.sections.find(([k]) => k === sectionKey) || meta.sections[0];
  }, [meta, sectionKey, isCustomSlot]);

  // When the user switches kind, seed the sectionKey + default title so
  // they don't have to retype a canonical section label.
  const changeKind = (k) => {
    setKind(k);
    setCustomKey("");
    if (k === "chapter") {
      setSectionKey("");
      setTitle((prev) => prev || "Chapter template");
    } else {
      const first = KIND_META[k].sections[0];
      setSectionKey(first[0]);
      setTitle(first[1]);
    }
  };
  const changeSection = (key) => {
    setSectionKey(key);
    if (key === "__custom__") {
      setCustomKey("");
      setTitle("");
      return;
    }
    const found = meta.sections?.find(([k]) => k === key);
    if (found) setTitle(found[1]);
  };

  // Normalize a user-typed custom key: lowercase, snake_case, ascii only.
  const _slugKey = (raw) =>
    (raw || "").trim().toLowerCase()
      .replace(/[^a-z0-9_ -]/g, "")
      .replace(/[\s-]+/g, "_")
      .slice(0, 48);

  const reset = () => {
    setName(""); setSubtitle(""); setDescription(""); setContent("");
    setCustomKey("");
    // iter-101 — also reset the sharing controls so the next preset
    // starts as Private unless the user re-ticks anything.
    setTeamOn(false); setWorkspaceIds(new Set());
    if (kind === "chapter") setTitle("Chapter template");
    else {
      const first = meta.sections[0];
      setSectionKey(first[0]); setTitle(first[1]);
    }
  };

  const save = async () => {
    if (!content.trim()) return toast.error("Add some content before saving.");
    if (!title.trim()) return toast.error("Give this preset a heading.");
    if (kind !== "chapter" && isCustomSlot && !_slugKey(customKey)) {
      return toast.error("Give the custom slot a key (letters, digits, underscores).");
    }
    setBusy(true);
    try {
      const body = {
        kind,
        title: title.trim(),
        content: content.trim(),
        name: name.trim() || title.trim(),
        // iter-101 — team + multi-workspace scope
        team_id: teamOn && team?.id ? team.id : null,
        workspace_ids: Array.from(workspaceIds),
      };
      if (kind !== "chapter") {
        body.key = isCustomSlot ? _slugKey(customKey) : sectionKey;
      }
      if (kind === "chapter") {
        if (subtitle.trim()) body.subtitle = subtitle.trim();
        if (description.trim()) body.description = description.trim();
      }
      const { data } = await api.post("/manual-presets", body);
      onCreated?.(data);
      toast.success(`Saved "${data.name}"`);
      reset();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this preset?")) return;
    try {
      await api.delete(`/manual-presets/${id}`);
      onDeleted?.(id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Group existing presets by kind for the library list beneath the editor.
  const byKind = useMemo(() => {
    const out = { front: [], back: [], chapter: [] };
    for (const p of (presets || [])) {
      if (p.kind && out[p.kind]) out[p.kind].push(p);
    }
    return out;
  }, [presets]);

  return (
    <section className="border border-[color:var(--border)] bg-white p-4"
             data-testid="pt-section-preset-editor">
      <button type="button" onClick={()=>setExpanded(!expanded)}
              className="w-full flex items-start justify-between gap-3 text-left"
              data-testid="pt-section-preset-toggle">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Section &amp; chapter presets</p>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">
            Author front-matter, back-matter and chapter templates directly here.
            Pick a section from the dropdown, paste the content, save — then load it
            into any book from the book editor.
          </p>
        </div>
        <span className="mt-1 text-[color:var(--text-muted)]">
          {expanded ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
        </span>
      </button>

      {expanded && (
        <>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Kind</span>
              <select value={kind} onChange={(e)=>changeKind(e.target.value)}
                      className="w-full border border-[color:var(--border)] px-2 py-1.5 text-sm"
                      data-testid="pt-preset-kind">
                <option value="front">Front-matter</option>
                <option value="back">Back-matter</option>
                <option value="chapter">Chapter</option>
              </select>
            </label>
            {kind !== "chapter" ? (
              <label className="block md:col-span-2">
                <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Section slot</span>
                <select value={sectionKey} onChange={(e)=>changeSection(e.target.value)}
                        className="w-full border border-[color:var(--border)] px-2 py-1.5 text-sm"
                        data-testid="pt-preset-section-key">
                  {meta.sections.map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                  <option value="__custom__">+ Custom slot (define your own)</option>
                </select>
                {isCustomSlot && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Custom key (slug)</span>
                      <input value={customKey} onChange={(e)=>setCustomKey(e.target.value)}
                             placeholder="e.g. author_intro"
                             className="w-full border border-[color:var(--border)] px-2 py-1.5 text-xs font-mono"
                             data-testid="pt-preset-custom-key"/>
                      <p className="text-[10px] text-[color:var(--text-muted)] mt-1">
                        Stored as <span className="font-mono">{_slugKey(customKey) || "(empty)"}</span>.
                        Books using this slot will keep it forever, even if you rename the preset later.
                      </p>
                    </div>
                    <div className="text-[11px] text-[color:var(--text-muted)] leading-relaxed">
                      Use custom slots for sections that aren't in the standard list — e.g. a Cast of Characters
                      opener, an Appendix, or a workbook worksheet index. The saved preset appears in the section-key
                      picker inside every book editor.
                    </div>
                  </div>
                )}
              </label>
            ) : (
              <label className="block md:col-span-2">
                <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Chapter subtitle (optional)</span>
                <input value={subtitle} onChange={(e)=>setSubtitle(e.target.value)}
                       placeholder='e.g. "A story in three acts"'
                       className="w-full border border-[color:var(--border)] px-2 py-1.5 text-sm"
                       data-testid="pt-preset-subtitle"/>
              </label>
            )}
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Preset name (picker label)</span>
              <input value={name} onChange={(e)=>setName(e.target.value)}
                     placeholder='e.g. "Medical disclaimer — RN"'
                     className="w-full border border-[color:var(--border)] px-2 py-1.5 text-sm"
                     data-testid="pt-preset-name"/>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Section heading</span>
              <input value={title} onChange={(e)=>setTitle(e.target.value)}
                     placeholder="Heading shown in the book"
                     className="w-full border border-[color:var(--border)] px-2 py-1.5 text-sm"
                     data-testid="pt-preset-title"/>
            </label>
          </div>

          {kind === "chapter" && (
            <label className="block mt-3">
              <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">TOC description (optional)</span>
              <input value={description} onChange={(e)=>setDescription(e.target.value)}
                     placeholder="Short blurb shown next to the TOC row"
                     className="w-full border border-[color:var(--border)] px-2 py-1.5 text-sm"
                     data-testid="pt-preset-description"/>
            </label>
          )}

          <label className="block mt-3">
            <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Content</span>
            <textarea rows={7} value={content} onChange={(e)=>setContent(e.target.value)}
                      placeholder="Paste the reusable content for this section…"
                      className="w-full border border-[color:var(--border)] px-2 py-1.5 font-reading text-sm focus:outline-none focus:border-[color:var(--primary)]"
                      data-testid="pt-preset-content"/>
          </label>

          {/* iter-101 — Share with… (Team + multi-select Workspaces).
              Tick as many workspaces as you like — every workspace
              member can LOAD the preset. */}
          <div className="mt-3 border border-[color:var(--border)] bg-[color:var(--canvas)]/50 p-3 space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Share with…</p>
            {team && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={teamOn}
                       onChange={(e)=>setTeamOn(e.target.checked)}
                       className="mt-0.5" data-testid="pt-preset-share-team"/>
                <div className="min-w-0">
                  <p className="text-xs font-medium">Team · <span className="text-[color:var(--text-muted)]">{team.name}</span></p>
                  <p className="text-[10px] text-[color:var(--text-muted)] mt-0.5">Every teammate can LOAD this preset.</p>
                </div>
              </label>
            )}

            <div>
              <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
                Workspaces <span className="normal-case tracking-normal text-[color:var(--text-muted)]/80">— tick every workspace this preset belongs to</span>
              </p>
              {(workspaces || []).length === 0 ? (
                <p className="text-[10px] text-[color:var(--text-muted)]" data-testid="pt-preset-share-empty">
                  You aren't in any workspace yet — invite a VA / editor to a
                  workspace (Library → Workspaces) so they show up here.
                </p>
              ) : (
                <div className="max-h-40 overflow-auto border border-[color:var(--border)] bg-white divide-y divide-[color:var(--border)]"
                     data-testid="pt-preset-share-workspaces">
                  {workspaces.map((w) => {
                    const checked = workspaceIds.has(w.id);
                    return (
                      <label key={w.id}
                             className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-[color:var(--canvas)]"
                             data-testid={`pt-preset-share-ws-${w.id}`}>
                        <input type="checkbox" checked={checked}
                               onChange={()=>toggleWorkspace(w.id)}
                               data-testid={`pt-preset-share-ws-${w.id}-cb`}/>
                        <span className="text-xs truncate">{w.name || "Workspace"}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-[color:var(--text-muted)] mt-1">
                {workspaceIds.size === 0 && !teamOn
                  ? "Private — only you will see this preset."
                  : workspaceIds.size === 0
                    ? "Shared with your team."
                    : `Shared with ${workspaceIds.size} workspace${workspaceIds.size === 1 ? "" : "s"}${teamOn ? " + team" : ""}. Only you can edit or delete it.`}
              </p>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <button onClick={save} disabled={busy || !content.trim() || !title.trim()}
                    className="btn-primary text-xs inline-flex items-center gap-1"
                    data-testid="pt-preset-save">
              <Save className="w-3 h-3"/> Save preset
            </button>
            <button onClick={reset} disabled={busy}
                    className="btn-ghost text-xs"
                    data-testid="pt-preset-reset">Reset form</button>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {["front", "back", "chapter"].map((k) => (
              <div key={k} className="border border-[color:var(--border)] bg-[color:var(--canvas)]/40 p-3"
                   data-testid={`pt-preset-list-${k}`}>
                <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">
                  {KIND_META[k].label} · {byKind[k].length}
                </p>
                {byKind[k].length === 0 ? (
                  <p className="text-[11px] italic text-[color:var(--text-muted)]">None yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {byKind[k].map((p) => {
                      const isOpen = !!openRows[p.id];
                      return (
                      <li key={p.id} className="text-xs"
                          data-testid={`pt-preset-item-${p.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <button type="button"
                                  onClick={()=>setOpenRows((prev)=>({...prev, [p.id]: !prev[p.id]}))}
                                  className="min-w-0 flex-1 text-left group"
                                  aria-expanded={isOpen}
                                  data-testid={`pt-preset-item-${p.id}-toggle`}>
                            <p className="font-medium truncate flex items-center gap-1 flex-wrap">
                              {isOpen
                                ? <ChevronDown className="w-3 h-3 shrink-0"/>
                                : <ChevronRight className="w-3 h-3 shrink-0"/>}
                              <span className="truncate">{p.name}</span>
                              {p.is_shared && (
                                <span className="text-[9px] font-normal uppercase tracking-widest px-1.5 py-0.5 bg-emerald-100 text-emerald-800"
                                      data-testid={`pt-preset-item-${p.id}-shared-badge`}>
                                  Shared
                                </span>
                              )}
                              {p.is_mine === false && (
                                <span className="text-[9px] font-normal uppercase tracking-widest px-1.5 py-0.5 bg-slate-100 text-slate-600"
                                      data-testid={`pt-preset-item-${p.id}-readonly-badge`}>
                                  Read-only
                                </span>
                              )}
                            </p>
                            <p className="text-[10px] text-[color:var(--text-muted)] truncate pl-4">
                              {p.key ? `${p.key}${p.title ? " · " + p.title : ""}` : (p.title || "")}
                            </p>
                          </button>
                          {p.is_mine !== false && (
                            <button onClick={()=>remove(p.id)}
                                    className="text-[color:var(--text-muted)] hover:text-red-700 shrink-0 mt-0.5"
                                    aria-label="Delete preset"
                                    data-testid={`pt-preset-item-${p.id}-delete`}>
                              <Trash2 className="w-3 h-3"/>
                            </button>
                          )}
                        </div>
                        {isOpen && (
                          <div className="mt-1 ml-4 border-l-2 border-[color:var(--border)] pl-2 space-y-1"
                               data-testid={`pt-preset-item-${p.id}-body`}>
                            {p.subtitle && (
                              <p className="text-[10px] italic text-[color:var(--text-muted)]">
                                Subtitle: {p.subtitle}
                              </p>
                            )}
                            {p.description && (
                              <p className="text-[10px] text-[color:var(--text-muted)]">
                                TOC: {p.description}
                              </p>
                            )}
                            <pre className="whitespace-pre-wrap font-reading text-[11px] leading-relaxed text-[color:var(--text)] bg-white border border-[color:var(--border)] p-2 max-h-64 overflow-auto"
                                 data-testid={`pt-preset-item-${p.id}-content`}>
                              {p.content || "(no content)"}
                            </pre>
                          </div>
                        )}
                      </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
